import { getAlmConnectionId, getAlmInfo } from "@/backend/read/BackendReader";
import type { BoardType } from "@/model/baseTypes";
import { currentRoute } from "@/router/navigation";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";
import { adminUrl } from "@/utils/env/Environment";

export function cockpitSessionUrl(
  options: { boardType?: BoardType; noNav?: boolean; boardId?: string; tab?: string } = {},
) {
  const sessionId = useSessionStore().current.id;
  if (!sessionId) {
    return cockpitPath("safepiplannings");
  }
  const path = boardTypeSetupPath[options.boardType!] ?? "generalInformation";
  const navQuery =
    (options.boardId ? "&boardId=" + options.boardId : "") +
    (options.tab ? "&tab=" + encodeURIComponent(options.tab) : "") +
    (options.noNav ? "&noNav" : "");
  return cockpitPath(`edit-session?id=${sessionId}&path=${path}${navQuery}`, {
    noFrom: options.noNav,
  });
}
export function cockpitProfileUrl(path?: string) {
  const sessionId = useSessionStore().current.id;
  path = encodeURIComponent(path || "");
  return sessionId && cockpitPath(path ? `profile?mapping=${sessionId}&path=${path}` : "profile");
}

export function cockpitConnectionUrl(teamId?: string) {
  const almConnectionId = getAlmConnectionId() || "";
  const almPath = getAlmInfo().cockpitPath;
  const teamPath = teamId && almConnectionId ? `/teams/${teamId}` : "";
  return cockpitPath(`alm-connections/${almPath}/${almConnectionId}${teamPath}`);
}

// The ALM connection's per-ART config page (where the Solution Train reference
// Kanban board is set). Mirrors cockpitConnectionUrl but scopes to an ART, with
// an optional tab to deep-link into (e.g. the Kanban mapping tab).
export function cockpitArtConnectionUrl(artId?: string, tab?: string) {
  const almConnectionId = getAlmConnectionId() || "";
  const almPath = getAlmInfo().cockpitPath;
  const artPath = artId && almConnectionId ? `/arts/${encodeURIComponent(artId)}` : "";
  const tabQuery = tab ? `?tab=${encodeURIComponent(tab)}` : "";
  return cockpitPath(`alm-connections/${almPath}/${almConnectionId}${artPath}${tabQuery}`);
}

export function cockpitBillingUrl() {
  return cockpitPath(useUserStore().isTraining ? "trainingBilling" : "billing");
}

export function cockpitRegisterUrl() {
  return cockpitPath("register", { noFrom: true });
}

export function cockpitCreateSessionUrl() {
  return cockpitPath("add-safepiplanning");
}

function cockpitPath(url: string, { noFrom = false } = {}) {
  const add = url.includes("?") ? "&" : "?";
  const from = noFrom ? "" : `${add}from=${encodeURIComponent(currentRoute().fullPath)}`;
  return `${adminUrl}/${url}${from}`;
}

const boardTypeSetupPath: Partial<Record<BoardType, string>> = {
  backlog: "programBacklogBoard",
  program: "programBoard",
  solution: "solutionPlanningBoard",
  solution_backlog: "solutionBacklogBoard",
  risk: "riskBoard",
  team: "teamBoards",
  flex: "flexBoards",
};
